import React, { useEffect, useMemo, useState } from 'react';
import { Alert } from '@one-thd/sui-atomic-components';
import { PricingPageLayout } from '../components/shared/PricingPageLayout';
import { ErrorResponse } from '../utils/errorUtils';
import {
  PRICING_BOOK_PAGE_LOAD,
  logNewRelicAction,
  newrelic,
} from '../utils/newrelic';
import { useRewardsAndOffers } from '../hooks/use-rewards-and-offers';
import { ERR_PRICING_BOOK } from '../constants';

interface PricingErrorPageProps {
  error?: ErrorResponse | { message: string };
}

/**
 * Dedicated error page for pricing-related errors
 * Provides consistent error display with page layout and left menu
 * Used when errors occur during benefit data loading
 *
 * This replaces the old pattern of showing errors embedded in content pages
 * Now provides a clean, dedicated error experience
 */
export const PricingErrorPage: React.FC<PricingErrorPageProps> = ({
  error,
}) => {
  const { generalError, rewardsAndOffers, showV2PriceBook } =
    useRewardsAndOffers();
  const [eventTriggered, setEventTriggered] = useState(false);

  const version = showV2PriceBook ? 'v2' : 'v1';

  // Memoize the error selection to avoid unnecessary recalculations
  const errorToLog = useMemo(
    () => generalError || error,
    [generalError, error],
  );

  useEffect(() => {
    // Prevent duplicate events - exit early if already triggered
    if (eventTriggered || !errorToLog) {
      return;
    }

    // Log specific case if error occurred because rewards data failed to load
    if (generalError && !rewardsAndOffers) {
      newrelic.addPageAction(PRICING_BOOK_PAGE_LOAD, {
        result: 'error',
        errorMessage: 'failure on the call to /rewardsandoffers',
        version,
      });
      setEventTriggered(true); // Mark as triggered to prevent duplicates
      return;
    }

    // If error is a GraphQL error, log with detailed info
    if (error && 'message' in error && 'graphQLErrors' in error) {
      logNewRelicAction(PRICING_BOOK_PAGE_LOAD, error as ErrorResponse, {
        version,
      });
      setEventTriggered(true); // Mark as triggered to prevent duplicates
      return;
    }

    // Otherwise log as a generic error message
    newrelic.addPageAction(PRICING_BOOK_PAGE_LOAD, {
      result: 'error',
      errorMessage: error?.message || 'Unknown error',
      version,
    });
    setEventTriggered(true); // Mark as triggered to prevent duplicates
  }, [
    errorToLog,
    error,
    generalError,
    rewardsAndOffers,
    eventTriggered,
    version,
  ]);

  return (
    <PricingPageLayout>
      {!generalError && (
        <div className="sui-flex sui-flex-col sui-gap-4">
          <Alert status="error">{ERR_PRICING_BOOK.message}</Alert>
        </div>
      )}
    </PricingPageLayout>
  );
};

PricingErrorPage.displayName = 'PricingErrorPage';
